import { useMemo, useState } from "react";
import * as api from "../lib/api";
import { useStore } from "../lib/store";
import type { Card, Note } from "../lib/types";

type Filter = "all" | "mining" | "leech" | "new";

function fold(s: string): string {
  return s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function describeSchedule(cards: Card[]): string {
  if (cards.length === 0) return "no cards";
  if (cards.every((c) => c.suspended)) return "suspended";
  const active = cards.filter((c) => !c.suspended);
  if (active.every((c) => c.reps === 0)) return "new";
  const soonest = Math.min(...active.map((c) => c.due));
  const days = Math.round((soonest - Date.now()) / 86_400_000);
  if (days <= 0) return "due";
  if (days < 31) return `${days}d`;
  if (days < 365) return `${Math.round(days / 30.44)}mo`;
  return `${(days / 365.25).toFixed(1)}y`;
}

export default function Browse() {
  const { notes, cards, sources, lang, profile, syncNow } = useStore();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<Note>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cardsByNote = useMemo(() => {
    const map = new Map<string, Card[]>();
    for (const card of cards) map.set(card.note_id, [...(map.get(card.note_id) ?? []), card]);
    return map;
  }, [cards]);

  const sourceById = useMemo(() => new Map(sources.map((s) => [s.id, s])), [sources]);

  const visible = useMemo(() => {
    const needle = fold(query.trim());
    return notes
      .filter((note) => {
        if (note.lang !== lang || note.status !== "active") return false;
        const noteCards = cardsByNote.get(note.id) ?? [];
        if (filter === "mining" && note.domain !== "mining") return false;
        if (filter === "leech" && !noteCards.some((c) => c.lapses >= profile.leechThreshold)) return false;
        if (filter === "new" && !noteCards.every((c) => c.reps === 0)) return false;
        if (!needle) return true;
        return (
          fold(note.term).includes(needle) ||
          fold(note.translation).includes(needle) ||
          fold(note.example ?? "").includes(needle)
        );
      })
      .sort((a, b) => b.created_at - a.created_at)
      .slice(0, 300);
  }, [notes, cardsByNote, lang, query, filter, profile.leechThreshold]);

  const leechCount = useMemo(
    () =>
      notes.filter(
        (n) =>
          n.lang === lang &&
          n.status === "active" &&
          (cardsByNote.get(n.id) ?? []).some((c) => c.lapses >= profile.leechThreshold),
      ).length,
    [notes, cardsByNote, lang, profile.leechThreshold],
  );

  async function saveEdit(id: string) {
    setBusy(true);
    setError(null);
    try {
      await api.updateNotes([{ id, ...draft }]);
      await syncNow();
      setEditing(null);
      setDraft({});
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await syncNow();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h1>Collection</h1>
      <p className="lede">{visible.length} shown</p>

      <input
        type="text"
        placeholder="Search terms, translations, examples"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      <div className="row wrap" style={{ marginTop: 10 }}>
        {(["all", "new", "mining", "leech"] as Filter[]).map((f) => (
          <button
            key={f}
            className={`btn small${filter === f ? " primary" : ""}`}
            onClick={() => setFilter(f)}
          >
            {f === "leech" ? `Leeches${leechCount ? ` (${leechCount})` : ""}` : f[0]!.toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {error && <div className="notice error" style={{ marginTop: 10 }}>{error}</div>}

      {filter === "leech" && (
        <p className="lede" style={{ marginTop: 12 }}>
          Cards that keep being forgotten. A leech is usually a badly written card, not a hard
          word — rewrite it or drop it rather than drilling it.
        </p>
      )}

      <div className="stack" style={{ marginTop: 14 }}>
        {visible.map((note) => {
          const noteCards = cardsByNote.get(note.id) ?? [];
          const suspended = noteCards.length > 0 && noteCards.every((c) => c.suspended);
          if (editing === note.id) {
            return (
              <div key={note.id} className="candidate">
                <div className="body stack">
                  <input
                    type="text"
                    aria-label="Term"
                    value={draft.term ?? note.term}
                    onChange={(e) => setDraft((d) => ({ ...d, term: e.target.value }))}
                  />
                  <input
                    type="text"
                    aria-label="Translation"
                    value={draft.translation ?? note.translation}
                    onChange={(e) => setDraft((d) => ({ ...d, translation: e.target.value }))}
                  />
                  <input
                    type="text"
                    aria-label="Example"
                    placeholder="Example sentence"
                    value={draft.example ?? note.example ?? ""}
                    onChange={(e) => setDraft((d) => ({ ...d, example: e.target.value || null }))}
                  />
                  <input
                    type="text"
                    aria-label="Usage note"
                    placeholder="Usage note"
                    value={draft.note ?? note.note ?? ""}
                    onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value || null }))}
                  />
                  <div className="row wrap">
                    <button className="btn small primary" disabled={busy} onClick={() => void saveEdit(note.id)}>Save</button>
                    <button className="btn small ghost" onClick={() => { setEditing(null); setDraft({}); }}>Cancel</button>
                    <button
                      className="btn small ghost"
                      disabled={busy}
                      onClick={() => void act(() => api.setSuspended([note.id], !suspended))}
                    >
                      {suspended ? "Unsuspend" : "Suspend"}
                    </button>
                    <button
                      className="btn small ghost danger"
                      disabled={busy}
                      onClick={() => void act(() => api.deleteNotes([note.id]))}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          }
          return (
            <div
              key={note.id}
              className="noterow"
              onClick={() => { setEditing(note.id); setDraft({}); }}
              style={{ cursor: "pointer", opacity: suspended ? 0.5 : 1 }}
            >
              <div className="body">
                <div className="term">
                  {note.kind === "cloze" ? note.cloze_text?.replace(/\{\{(.*?)\}\}/, "____") : note.term}
                </div>
                <div className="translation">
                  {note.kind === "cloze" ? note.cloze_answer : note.translation}
                </div>
                <div className="tags">
                  {note.domain === "mining" && <span className="tag mining">mining</span>}
                  {note.kind === "cloze" && <span className="tag cloze">grammar</span>}
                  {note.source_id && sourceById.get(note.source_id) && (
                    <span className="tag">{sourceById.get(note.source_id)!.title.slice(0, 28)}</span>
                  )}
                </div>
              </div>
              <span className="schedule">{describeSchedule(noteCards)}</span>
            </div>
          );
        })}
      </div>

      {visible.length === 0 && (
        <div className="empty"><p>Nothing matches.</p></div>
      )}
    </>
  );
}
